import { Link } from 'react-router-dom';
import { PolicyMeta } from '../components/PolicyMeta';
import { perDay, useSite } from '../site/SiteProvider';

/**
 * プライバシーポリシー（`/privacy`）。doc/AUTH.md §13 で決めた「いま実際に持っているもの」を本文にしたもの。
 *
 * **書いていないものを保存しない、保存しているものは全部書く。**
 * 表の中身は `services/api/src/lib/store.ts` の型と揃える。保存するものを足したら、ここも足す。
 * この本文を直したら、`services/api/src/lib/terms.ts` の `PRIVACY_VERSION` を上げる。
 *
 * 広告（アフィリエイト・AdSense）はいまは出していないが、載せる前提で §5 の広告の配信事業者はいつも書く（載せるたびに同意を取り直さないため）。
 * **広告の配信事業者を足す・外すときは `PRIVACY_VERSION` を上げる**（本文が変わるので、同意を取り直す）。アクセス解析（§5。services/api の lib/analytics.ts）も同じ。
 */
export function PrivacyPage() {
  const { limits } = useSite();
  return (
    <main className="page policy">
      <h1 className="page__title">プライバシーポリシー</h1>
      <PolicyMeta kind="privacy" />
      <p className="page__lead">
        GenDan（Generative AI Shindan）が何を預かり、どのくらい持っていて、誰に渡すのかを書きます。あわせて <Link to="/terms">利用規約</Link> もご覧ください。
      </p>

      <section className="section">
        <h2 className="section__title">1. はじめに、保存していないもの</h2>
        <p>
          <strong>氏名・電話番号はお尋ねしていませんし、保存していません。</strong>
          ログインしている方の表示名やメールアドレスは、ログインのたびに受け取って画面に出しているだけです。診断の結果にも、表示名やメールアドレスは入れていません。
          <strong>メールアドレスを保存するのは、お問い合わせで返信先を書いていただいたときだけ</strong>です（2. の表）。
          ただし、運営の管理者には、利用者の一覧を開いたときに、ログインの仕組み（Firebase Authentication）からメールアドレスと表示名を読んで見せています（そのときも保存はしません）。
        </p>
        <p>
          <strong>LINE でログインした方</strong>は、LINE（LY 株式会社）から <strong>LINE のユーザー ID と表示名</strong> を受け取ります。
          ユーザー ID は利用者 ID の一部として使い、表示名はログインの仕組み（Firebase Authentication）に表示名として登録します。
          <strong>LINE からメールアドレスは受け取っていません。</strong>
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">2. 保存しているもの</h2>
        <div className="policy__table-wrap">
          <table className="policy__table">
            <thead>
              <tr><th>保存しているもの</th><th>いつ作られるか</th><th>どのくらい持つか</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>診断の途中の回答（受検中の一時的な控え）</td>
                <td>診断を始めたとき</td>
                <td>24 時間</td>
              </tr>
              <tr>
                <td>診断の結果（タイプ・キャラクター・軸の点数・性格の側面）と、<strong>項目ごとの回答</strong></td>
                <td>診断を終えたとき</td>
                <td>ご自身で消すか、消すご依頼をいただくまで（サービスを終えるときは消します）</td>
              </tr>
              <tr>
                <td>利用者 ID（uid）・権限・絵を描いた回数・相性の AI の読みを頼んだ回数</td>
                <td>ログインしたとき</td>
                <td>絵の回数は直近 7 日ぶん、AI の読みの回数は 3 日。ほかは退会まで</td>
              </tr>
              <tr>
                <td><strong>相性の AI の読み</strong>（比べた 2 つの結果 ID・選んだ関係・AI が書いた文章）。<strong>利用者 ID とは結びつけません</strong></td>
                <td>相性の詳細版で AI の読みを頼んだとき</td>
                <td>どちらかの結果が消えるまで（結果を消すと一緒に消します）</td>
              </tr>
              <tr>
                <td>作った診断（テーマの題・キャラクター・絵）と、作った方の利用者 ID</td>
                <td>診断を作ったとき</td>
                <td>消すまで</td>
              </tr>
              <tr>
                <td><strong>ログインしていない方の作成回数</strong>（ブラウザの識別子と IP アドレスの<strong>ハッシュ</strong>）</td>
                <td>ログインせずに診断を作ったとき</td>
                <td>3 日</td>
              </tr>
              <tr>
                <td>利用規約・プライバシーポリシーに同意した版と日時</td>
                <td>同意したとき</td>
                <td>退会まで</td>
              </tr>
              <tr>
                <td><strong>お問い合わせの内容</strong>と、送った方の利用者 ID。<strong>返信先を書いていただいた場合はそれも</strong></td>
                <td>お問い合わせのフォームから送ったとき</td>
                <td>返信先は<strong>対応が済んだときに消します</strong>。内容はそのあとも残り、アカウントを削除すると消します</td>
              </tr>
              <tr>
                <td><strong>相性ドラマの動画のリクエスト</strong>（どの診断の、どのキャラ 2 人か・見たい場面の文）と、送った方の利用者 ID</td>
                <td>診断の入口からリクエストしたとき</td>
                <td>取り下げても・見送っても残り、アカウントを削除すると消します。作った動画は運営のものとして残ります（あなたとは結びつけません）</td>
              </tr>
              <tr>
                <td><strong>診断の通報</strong>（どの診断か・理由・ひとこと・送ったページの結果 ID）。<strong>送った方のことは記録しません</strong></td>
                <td>入口や結果のページから通報したとき</td>
                <td>当面のあいだ</td>
              </tr>
              <tr>
                <td>操作の記録（誰が・いつ・何を消した／止めた／送った）</td>
                <td>管理者が操作したとき</td>
                <td>当面のあいだ</td>
              </tr>
              <tr>
                <td>日ごとの件数（何回受けられたか、何枚描いたか）</td>
                <td>随時</td>
                <td>当面のあいだ。個人とは結びついていません</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="policy__note">
          <strong>項目ごとの回答を保存しているのは、診断そのものの信頼性を調べるため</strong>です（何問がどれだけ揃っているか、もう一度受けたときに同じ結果になるか）。個人を特定できる情報は入っておらず、まとめて統計として扱います。
          API から取り出せる形にはしていません。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">3. ブラウザに置いているもの</h2>
        <ul className="policy__list">
          <li><strong>ログインしているかどうかの印</strong>。次に来たときに読み込みを速くするために使います。</li>
          <li><strong>ブラウザの識別子</strong>。ログインせずに診断を作れる回数（いまは {perDay(limits.anonThemeDailyLimit, '回', '0 回。ログインしないと作れません')}）を数えるためのものです。サーバにはハッシュにして送られます。</li>
          <li><strong>作った診断の合言葉</strong>。ログインせずに作った診断を、あとからログインして引き取るためのものです。</li>
          <li><strong>LINE でログインする間だけの Cookie</strong>。ほかの人が始めたログインを踏まされないよう、LINE から戻ってきたときに照合する使い捨ての値です。戻ってきたときに消し、遅くとも 10 分で切れます。</li>
          <li><strong>不正な利用を防ぐ仕組み（reCAPTCHA）が置くもの</strong>。診断を作る欄を使ったときなどに、Google の reCAPTCHA が Cookie を置き、確認が済んだ印（1 時間ほどで切れます）をブラウザに残します。くわしくは 5. の「不正な利用を防ぐ仕組み」に書きます。</li>
          <li><strong>アクセス解析（Google Analytics）の Cookie</strong>。同じブラウザからの再訪を見分けるための識別子です（最長 2 年で切れます）。くわしくは 5. の「アクセス解析」に書きます。</li>
        </ul>
        <p>
          いずれも、そのブラウザの中に残ります（reCAPTCHA と Google Analytics の Cookie は Google にも送られます）。ブラウザのデータを消せば、あわせて消えます。
          広告の配信事業者が置く Cookie については、5. の「広告の配信事業者」に書きます。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">4. IP アドレスの扱い</h2>
        <p>
          ログインせずに診断を作れる回数と、診断を通報できる回数を数えるときに、IP アドレスを使っています。
          <strong>そのままの形では保存せず、日付を混ぜたハッシュにして、3 日で消しています。</strong>
          ふだん効くのはブラウザの識別子のほうで、IP は「識別子を消して作り直す」使われ方に備えた二番目の網です。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">5. 誰に渡るか</h2>
        <h3 className="policy__sub">Gemini（Google Cloud の Vertex AI）に送るもの</h3>
        <p>
          診断を作るときに送るのは、<strong>あなたが書いたテーマの文章と、キャラクターを考えてもらうための指示だけ</strong>です。
          <strong>設問への回答や診断の結果は、診断を作るときにも受けるときにも送っていません。</strong>
        </p>
        <p>
          ただし、ログインして使う「相性の詳細版」で <strong>AI の読み</strong> を頼んだときは、2 人の結果のうち
          <strong>はっきり違う・そろって同じ側に寄る側面の名前とスコア、選んだ相手との関係</strong>（恋人・上司など）を送ります。
          設問への回答そのもの・氏名・メールアドレス・ログインの ID は送りません。書かれた文章は、同じ組み合わせで二度書かないよう保存し、どちらかの結果を消すと一緒に消します。
        </p>
        <h3 className="policy__sub">不正な利用を防ぐ仕組み（reCAPTCHA Enterprise・Firebase App Check）</h3>
        <p>
          診断を作る・キャラクターの絵を描く・相性の AI の読みを頼む・問い合わせや通報を送る・動画をリクエストするときに、
          <strong>本物のブラウザからの操作かを確かめるため</strong>、Google の reCAPTCHA Enterprise と Firebase App Check を使っています。
          このとき、ブラウザから Google に、<strong>IP アドレス・Cookie などの識別子・ブラウザや端末の種類・ページの上での操作の様子</strong>が送られます。
          使うのは機械による大量の利用を防ぐためだけで、このサイトが広告に使うことはありません。画面にパズルなどは出ません（右下に reCAPTCHA の印が出ます）。
          Google の扱いは <a href="https://policies.google.com/privacy?hl=ja" target="_blank" rel="noreferrer">Google のプライバシーポリシー</a> と
          <a href="https://policies.google.com/terms?hl=ja" target="_blank" rel="noreferrer">利用規約</a> に従います。
        </p>
        <h3 className="policy__sub">アクセス解析（Google Analytics）</h3>
        <p>
          どのページがどれだけ見られているか、どこから来ていただいたかを知ってサイトを良くするために、Google の Google Analytics を使っています。
          ページを開くと、ブラウザから Google に、<strong>開いたページの URL・ひとつ前のページ・Cookie などの識別子・IP アドレス・ブラウザや端末の種類・おおまかな地域・ページの上での操作の様子</strong>が送られます。
          <strong>診断の回答・利用者 ID・氏名・メールアドレスは送っていません。</strong>結果ページの URL は送られますが、運営が数を見るためだけに使います。
          運営が見るのは集計した数字で、Google シグナルや広告のパーソナライズには使っていません。
          止めたいときは <a href="https://tools.google.com/dlpage/gaoptout?hl=ja" target="_blank" rel="noreferrer">Google アナリティクス オプトアウト アドオン</a> を使うか、ブラウザで Cookie を受け付けないようにしてください。
          Google の扱いは <a href="https://policies.google.com/technologies/partner-sites?hl=ja" target="_blank" rel="noreferrer">Google のポリシー</a> に書かれています。
        </p>
        <AdVendors />
        <h3 className="policy__sub">そのほか</h3>
        <p>
          サーバとデータの置き場として Google Cloud（Cloud Run・Firestore・Cloud Storage）を、ログインの仕組みとして Firebase Authentication を、独自ドメインの配信に Firebase Hosting を使っています。
          LINE でのログインには、LY 株式会社の LINE ログインを使っています（LINE に渡るのは、ログインの手続きに要る値だけです）。
          法令に基づく場合を除いて、上に書いたアクセス解析・広告の配信事業者と、これ以外の第三者に渡すことはありません。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">6. 結果ページの見え方</h2>
        <p>
          診断の結果ページは <strong>URL を知っている方だけ</strong> が見られます。検索には出ません。氏名やメールアドレスは入っていないので、URL を伝えない限り、誰の結果かは分かりません。ただし <strong>URL を知られると誰でも見られる</strong> ことは、SNS に貼るときに気に留めておいてください。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">7. お問い合わせで預かるもの</h2>
        <p>
          <Link to="/contact">お問い合わせのフォーム</Link> はログインした方だけが使えます。どなたからかは利用者 ID で分かるので、<strong>氏名はお尋ねしません</strong>。返信先のメールアドレスは <strong>任意</strong> で、書いていただいた場合は <strong>返信のためだけ</strong> に使い、対応が済んだときに消します。空のままでも送れます（そのときはお返事ができません）。
        </p>
        <p>
          いただいた内容は、運営者だけが管理メニューで読みます。荒らしを防ぐため、1 人 1 日に送れる通数を決めています（いまは {limits.contactDailyLimit} 通）。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">8. 消したいとき</h2>
        <ul className="policy__list">
          <li><strong>作った診断を消す</strong>: 自分のページの「作った診断」か、その診断の入口ページからできます。すでに出ている結果ページは残ります。</li>
          <li><strong>診断の結果を消す</strong>: ログインして受けた結果は、<Link to="/me">自分のページ</Link> の「受けた診断」から消せます。項目ごとの回答も一緒に消えます。ログインせずに受けた結果は <Link to="/contact">お問い合わせ</Link> からご依頼ください（結果ページの URL を添えてください）。</li>
          <li><strong>ブラウザに置いたもの</strong>: ブラウザのデータを消せば、あわせて消えます。</li>
          <li>
            <strong>アカウントを削除する</strong>: <Link to="/me?tab=account">自分のページ</Link> の「アカウント」からできます。本人確認のためにもう一度ログインしていただき、
            ログインして受けた結果（回答ごと）・お問い合わせ・ログインの記録を消します。作った診断は新しく受けられなくなります（すでに出ている結果ページは残ります）。
          </li>
        </ul>
      </section>

      <section className="section">
        <h2 className="section__title">9. このポリシーの変更</h2>
        <p>
          保存するものが変わるときは、この本文と「版」を更新します。変更したあとの最初のログインで、もう一度同意をお願いします。
        </p>
      </section>

      <section className="section">
        <h2 className="section__title">10. お問い合わせ</h2>
        <p>
          個人情報の削除・開示のご依頼、その他のお問い合わせは、
          <Link to="/contact">お問い合わせのフォーム</Link> からお送りください（ログインが必要です）。運営は x-type です。
        </p>
        <div className="page__actions">
          <Link className="button button--primary" to="/contact">お問い合わせ</Link>
          <Link className="button button--ghost" to="/terms">利用規約</Link>
          <Link className="button button--ghost" to="/help">ヘルプ</Link>
        </div>
      </section>
    </main>
  );
}

/**
 * 広告の配信事業者。AdSense のポリシーが求める記載（第三者の Cookie・オプトアウトの方法）と、
 * 電気通信事業法の外部送信規律で求められる「送り先・送るもの・目的」をまとめて書く。
 * **ASP を足したら、ここの表にも足す。**
 */
function AdVendors() {
  return (
    <>
      <h3 className="policy__sub">広告の配信事業者</h3>
      <p>
        <strong>いまは広告を出していません。</strong>運営を続けるために、今後広告（アフィリエイト広告を含みます）を載せることがあります。載せるときは、枠に「広告」「PR」と書きます。
        広告を載せているあいだは、広告を表示したり、広告から商品を申し込んだことを確かめたりするために、<strong>ページを開くと、ブラウザから次の事業者へ情報が送られます</strong>。
        <strong>診断の回答や結果・利用者 ID は送っていません。</strong>
      </p>
      <div className="policy__table-wrap">
        <table className="policy__table">
          <thead>
            <tr><th>送り先</th><th>送られるもの</th><th>目的</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Google（Google AdSense）</td>
              <td>開いたページの URL、Cookie などの識別子、IP アドレス、ブラウザの種類</td>
              <td>広告の表示、閲覧履歴にもとづく広告の出し分け、表示回数・クリックの計測、不正の防止</td>
            </tr>
            <tr>
              <td>アフィリエイトの事業者（A8.net・もしもアフィリエイト・バリューコマース・Amazon アソシエイト・楽天アフィリエイトなど、広告ごとに異なります）</td>
              <td>広告を表示した・押したこと、Cookie などの識別子、IP アドレス、ブラウザの種類</td>
              <td>表示回数と、広告から申し込みがあったかの計測（成果報酬の確認）</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Google を含む第三者の配信事業者は、Cookie を使って、このサイトや他のサイトへのこれまでのアクセスにもとづく広告を配信します。
        閲覧履歴にもとづく広告（パーソナライズ広告）は、<a href="https://adssettings.google.com/" target="_blank" rel="noreferrer">Google の広告設定</a> で止められます。
        Google がどのように情報を使うかは、<a href="https://policies.google.com/technologies/partner-sites?hl=ja" target="_blank" rel="noreferrer">Google のポリシー</a> に書かれています。
        ほかの配信事業者の Cookie は、<a href="https://optout.aboutads.info/" target="_blank" rel="noreferrer">aboutads.info</a> からまとめて止められます。
      </p>
      <p>ブラウザの設定で Cookie を消したり受け付けないようにしたりもできます。そのときも診断はふつうに受けられます。</p>
    </>
  );
}
